import { useCallback, useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import type { SendOtp } from '@/modules/auth/use-cases'
import {
  EmailLinkedToDifferentAccountError,
  NetworkError,
  OtpSendFailedError,
  isOtpSendRefusal,
} from '@/shared/domain'
import { useOtpSendCooldown, useSubmitGuard } from '@/shared/ui/hooks'
import { isValidEmail } from '@/modules/auth/ui/validation'

export type UseSignupPageProps = {
  onSendOtp: SendOtp
}

export type SignupStep = 'identifier' | 'otp'

export type UseSignupPageResult = {
  step: SignupStep
  email: string
  identifier: string
  isSubmitting: boolean
  cooldownSeconds: number
  isCooldownActive: boolean
  hasError: boolean
  errorMessage: string
  setEmail: (v: string) => void
  handleSubmit: (e: FormEvent) => void
  backToIdentifier: () => void
  handleOtpSuccess: () => Promise<void>
}

export function useSignupPage({ onSendOtp }: UseSignupPageProps): UseSignupPageResult {
  const [step, setStep] = useState<SignupStep>('identifier')
  const [email, setEmail] = useState<string>('')
  const { isSubmitting, runGuarded } = useSubmitGuard()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const navigate = useNavigate()

  const identifier = email.trim()
  const {
    secondsLeft: cooldownSeconds,
    applyRefusal,
    recordSend,
  } = useOtpSendCooldown({
    channel: 'email',
    identifier,
  })

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (cooldownSeconds > 0) return
    if (identifier === '') {
      setErrorMessage('Enter your email')
      return
    }
    if (!isValidEmail(identifier)) {
      setErrorMessage('Enter a valid email')
      return
    }
    setErrorMessage('')
    await runGuarded(async () => {
      try {
        await onSendOtp({ identifier, channel: 'email', allowCreate: true })
        recordSend()
        setStep('otp')
      } catch (err) {
        if (err instanceof EmailLinkedToDifferentAccountError) {
          setErrorMessage(err.message)
        } else if (isOtpSendRefusal(err)) {
          setErrorMessage(err.message)
          applyRefusal(err)
        } else if (err instanceof OtpSendFailedError) {
          setErrorMessage(err.message)
        } else if (err instanceof NetworkError) {
          setErrorMessage('Network error. Please try again.')
        } else {
          setErrorMessage('Something went wrong. Please try again.')
        }
      }
    })
  }

  const backToIdentifier = () => {
    setStep('identifier')
    setErrorMessage('')
  }

  const handleOtpSuccess = useCallback(async () => {
    navigate('/register', { replace: true })
  }, [navigate])

  return {
    step,
    email,
    identifier,
    isSubmitting,
    cooldownSeconds,
    isCooldownActive: cooldownSeconds > 0,
    hasError: errorMessage !== '',
    errorMessage,
    setEmail,
    handleSubmit,
    backToIdentifier,
    handleOtpSuccess,
  }
}
